import React from "react";
import {
  Loader,
  LoaderSizes,
  Button,
  Iconography,
  IconographySizes,
  Pagination,
  Notification,
  NotificationVariants,
} from "@hid-galaxy-ui/galaxy-react";
import { useAppSelector, useAppDispatch } from "../../../../../../redux";
import { userSliceAction } from "../../../../../../redux/slices/user";
import PartnerUserFormModal from "../../../../../../common/components/PartnerUserFormModal";
import { UserListType } from "../../../../../../redux/slices/user/Types";
import PartnerUserCard from "../../../../../../common/components/PartnerUserCard";
import { sendAccountInviteSagaAction } from "../../../../../../redux/saga/user/saga-actions";
import { usePartnerTabPermission } from "../../../../../../contexts/PartnerPermissionContext";
import "./PartnerUsers.scss";

const PartnerUsers = () => {
  const [currentPageNumber, setCurrentPageNumber] = React.useState<number>(1);
  const [addModalOpen, setAddModalOpen] = React.useState<boolean>(false);
  const [updateModalOpen, setUpdateModalOpen] = React.useState<boolean>(false);
  const [selectedUser, setSelectedUser] = React.useState<any | null>(null);
  const [toasts, setToasts] = React.useState<any[]>([]);
  const { userList, status } = useAppSelector((state) => state.user);
  const { partnerDetails } = useAppSelector((state) => state.partner_details);
  const dispatch = useAppDispatch();
  const isPartnerInactive = partnerDetails?.status === "Inactive";
  const { readOnly: tabReadOnly } = usePartnerTabPermission();
  const showUserActions = !isPartnerInactive;

  React.useEffect(() => {
    if (
      status.sendAccountInviteStatus !== "idle" &&
      status.sendAccountInviteMessage
    ) {
      const isSuccess = status.sendAccountInviteStatus === "success";
      setToasts([
        {
          title: isSuccess ? "Account Invite Sent" : "Error",
          message: status.sendAccountInviteMessage,
          variant: isSuccess
            ? NotificationVariants.Success
            : NotificationVariants.Error,
          icon: (
            <Iconography
              icon={isSuccess ? "check" : "ban"}
              size={IconographySizes.Medium}
            />
          ),
          expanded: true,
        },
      ]);
      dispatch(userSliceAction.resetSendAccountInviteStatus());
    }
  }, [
    status.sendAccountInviteStatus,
    status.sendAccountInviteMessage,
    dispatch,
  ]);

  const handlePageChange = (clickedPageNumber: number | null) => {
    setCurrentPageNumber(clickedPageNumber);
  };

  const pageSize = 6;
  const totalRecords = userList.length;

  const paginatedData = userList.slice(
    (currentPageNumber - 1) * pageSize,
    currentPageNumber * pageSize
  );

  const onAddUserClick = () => {
    setUpdateModalOpen(false);
    setAddModalOpen(true);
  };

  const onEditUserClick = (user: UserListType[0]) => {
    setSelectedUser(user);
    setAddModalOpen(false);
    setUpdateModalOpen(true);
  };

  const handleInviteUser = (user: UserListType[0]) => {
    dispatch(
      sendAccountInviteSagaAction({
        ID: user.ID,
        userEmail: user.email,
      })
    );
  };

  const renderModals = () => (
    <>
      {addModalOpen && (
        <PartnerUserFormModal setModalOpen={setAddModalOpen} mode="Add" />
      )}
      {updateModalOpen && (
        <PartnerUserFormModal
          user={selectedUser}
          setModalOpen={setUpdateModalOpen}
          mode="Update"
        />
      )}
    </>
  );

  if (status.userListLoading && userList.length === 0) {
    return (
      <>
        <Notification toasts={toasts} setToasts={setToasts} />
        <div className="partner-users-container">
          <div className="loading">
            <Loader variant={LoaderSizes.large} />
          </div>
        </div>
        {renderModals()}
      </>
    );
  }

  if (userList.length === 0) {
    return (
      <>
        <Notification toasts={toasts} setToasts={setToasts} />
        <div className="partner-users-container">
          <div className="no-partner-users">
            <Iconography icon="file" size={IconographySizes.Large} />
            <p>No Users Added For Partner</p>
            {showUserActions && (
              <Button
                variant="primary"
                label="Add New User"
                icon={
                  <Iconography icon="userPlus" size={IconographySizes.Medium} />
                }
                onClick={onAddUserClick}
                disabled={tabReadOnly}
              />
            )}
          </div>
        </div>
        {renderModals()}
      </>
    );
  }

  return (
    <>
      <Notification toasts={toasts} setToasts={setToasts} />
      <div className="partner-users-container">
        <div className="user-list-container">
          <div className="title">
            <p>Partner Users</p>
            {showUserActions && (
              <Button
                variant="primary"
                label="Add New User"
                icon={
                  <Iconography icon="userPlus" size={IconographySizes.Medium} />
                }
                onClick={onAddUserClick}
                disabled={tabReadOnly}
              />
            )}
          </div>
          <div className="user-list-wrapper">
            {paginatedData.map((user) => (
              <PartnerUserCard
                user={user}
                onEditUserClick={onEditUserClick}
                onInvite={handleInviteUser}
                inviteLoading={status.sendAccountInviteLoading}
                key={user.ID}
              />
            ))}
          </div>
          <Pagination
            currentPageNumber={currentPageNumber}
            totalRecords={totalRecords}
            pageSize={pageSize}
            border={true}
            onPageNumberClick={handlePageChange}
          />
        </div>
      </div>
      {renderModals()}
    </>
  );
};

export default PartnerUsers;
